<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: smallScreen,
        [$style.mediumScreen]: $screen === 'm' && !previewMode,
        [$style.largeScreen]: $screen === 'l' && !previewMode,
        [$style.previewMode]: previewMode,
        [$style.noPadding]: noPadding,
        [$style.white]: white
      }
    ]"
  >
    <div
      v-if="loading"
      :class="$style.loadingContent"
    >
      <BaseSpinner />
    </div>

    <div
      v-else
      :class="$style.content"
    >
      <div
        v-if="smallScreen"
        :class="$style.mobileTop"
      >
        <slot name="mobileTop" />
      </div>
      <div
        v-if="!loading"
        :class="$style.side"
      >
        <slot name="aside" />
        <ModalDetails
          v-if="details && details.length"
          :details="details"
        />
        <Actions
          v-if="actions.length"
          :actions="actions"
          @select="$emit('action', $event)"
        />
      </div>
      <div
        v-if="!loading"
        :class="$style.nav"
      >
        <slot name="nav" />
      </div>
      <div
        ref="scrollContainer"
        :class="$style.main"
        v-test="'_base-modal-scrollContainer'"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import ModalDetails from '@/components/_shared/ModalDetails.vue';
import Actions from '@/components/_shared/modal-actions/index.vue';
import { usePageScroll } from '@/helpers/scroll';
import { usePageLayoutStore } from '@/stores/page-layout';

const props = defineProps<{
  actions: any[];
  details: any[];
  noPadding: boolean;
  loading: boolean;
  previewMode: boolean;
  white: boolean;
}>();

defineEmits(['action']);

const scrollContainer = ref();
usePageScroll(scrollContainer);

const smallScreen = computed(() => {
  const { screenSize } = usePageLayoutStore();
  return screenSize === 's' || props.previewMode;
});
</script>

<style
  lang="scss"
  module
>
$loadingHeight: 150px;

.base {
  background-color: var(--surface-secondary);

  &.white {
    background-color: var(--surface-primary);
  }
}

.loadingContent {
  position: relative;
  height: 150px;
}

.content {
  .base:not(.smallScreen) & {
    display: flex;
    align-items: stretch;
    flex-direction: row-reverse; // Reversing the order in the DOM so we can target .side:empty ~ .main
    height: 100%;
    overflow-y: auto;
  }

  .base.previewMode & {
    height: 100%;
    overflow-y: auto;
  }
}

.main {
  height: 100%;
  min-height: $loadingHeight;
  position: relative;

  .base:not(.smallScreen) & {
    overflow-y: auto;
  }

  .base.largeScreen:not(.noPadding) & {
    padding: $spacing * 1.5;
  }

  .base.mediumScreen:not(.noPadding) & {
    padding: $spacing;
  }

  .base.smallScreen:not(.noPadding) & {
    padding: $spacing * 0.5;
  }

  .side:not(:empty) ~ & {
    .base.largeScreen & {
      width: 75%;
    }

    .base.mediumScreen & {
      width: 67%;
    }
  }

  .base.smallScreen &,
  .side:empty ~ & {
    width: 100%;
  }
}

.mobileTop {
  padding: $spacing;

  &:empty {
    display: none;
  }
}

.nav {
  &:empty {
    display: none;
  }

  order: 1;

  .base.white & {
    border-right: 1px solid var(--border);
  }

  .base:not(.smallScreen) & {
    overflow-y: auto;
    min-width: 400px;
    flex-basis: 400px;
    background-color: var(--surface-primary);
  }

  .base.mediumScreen & {
    min-width: auto;
  }

  .base.smallScreen & {
    display: flex;
    justify-content: stretch;
    background-color: var(--surface-secondary);
  }
}

.side {
  align-self: stretch;
  overflow-y: auto;

  .base:not(.smallScreen) & {
    background-color: var(--surface-primary);
    border-radius: 0 0 $modal-radius 0;
    border-left: 1px solid var(--border);
  }

  &:not(:empty) {
    .base.largeScreen & {
      width: 25%;
    }

    .base.mediumScreen & {
      width: 33%;
    }
  }

  &:empty {
    display: none;
  }

  .base.smallScreen & {
    width: 100%;
  }
}
</style>
